import { CommandType } from '@shared/constants/CommandValues';
import EditProfileContainer from '@shared/hooks/state/EditProfileContainer';
import { useHotkeyForCopyMacro } from '@shared/hooks/useHotkeyForCopyMacro';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Encapsulates all macro list manipulation logic shared between
 * CreateOrUpdateProfilePage and OrgProfileEditPage.
 */
export function useMacroEditing() {
    const { t } = useTranslation();
    const editProfileContainer = EditProfileContainer.useContainer();
    const prepareHotkeysForCopyMacro = useHotkeyForCopyMacro();
    const [macroToAddCommand, setMacroToAddCommand] = useState<MacroDisplayModel | undefined>();
    const [editingMacroIndex, setEditingMacroIndex] = useState<number | undefined>();

    // ── Search ────────────────────────────────────────────────────────────────

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        editProfileContainer.setFilter(e.target.value);
    };

    const handleClearSearch = () => {
        editProfileContainer.setFilter('');
    };

    // ── Macro-level actions ───────────────────────────────────────────────────

    const copyMacro = (macro: MacroDisplayModel) => {
        const macros = editProfileContainer.macros;
        editProfileContainer.setHasChanges(true);
        const cloneMacro = _.cloneDeep(macro);
        const preparedCopy = prepareHotkeysForCopyMacro(cloneMacro, editProfileContainer.hotkeys);
        macros.unshift({ ...preparedCopy.newMacro });
        editProfileContainer.updateMacros([...macros]);
    };

    const handleRemoveMacro = (macro: MacroDisplayModel) => {
        const macros = editProfileContainer.macros;
        editProfileContainer.setHasChanges(true);
        macro.commands?.forEach((c) => {
            if (c.commandType === 'hotkey') {
                editProfileContainer.removeHotkeyFromMacro(c);
            }
        });
        _.remove(macros, (h) => h === macro);
        editProfileContainer.updateMacros([...macros]);
    };

    const handleMacroChange = (updatedMacro: MacroDisplayModel) => {
        const macros = editProfileContainer.macros;
        const index = macros.findIndex((m) => m.id === updatedMacro.id);
        if (index !== -1) {
            macros[index] = updatedMacro;
            editProfileContainer.updateMacros([...macros]);
        }
        editProfileContainer.setHasChanges(true);
    };

    const handleNameChange = (text: string, macro: MacroDisplayModel) => {
        const macros = editProfileContainer.macros;
        editProfileContainer.setHasChanges(true);
        macro.name = text;
        editProfileContainer.updateMacros([...macros]);
    };

    // ── Step actions ─────────────────────────────────────────────────────────

    const handleAddStep = (macroIndex: number) => {
        editProfileContainer.setHasChanges(true);
        const macros = editProfileContainer.macros;
        macros[macroIndex].events.push({
            eventType: 'KeyPress',
            keys: [''],
            holdTimeMilliseconds: 0,
        });
        editProfileContainer.updateMacros([...macros]);
    };

    // ── Command actions ───────────────────────────────────────────────────────

    const handleCommandChange = (value: string, macro: MacroDisplayModel, index: number) => {
        editProfileContainer.setHasChanges(true);
        const originalValue = macro.commands[index].command;
        macro.commands[index].command = value;
        if (macro.commands[index].commandType === 'hotkey') {
            const hotkeyId = editProfileContainer.updateHotkeyFromMacro(value, macro, index, originalValue);
            macro.commands[index].hotkeyLocalId = hotkeyId ?? null;
        }
        editProfileContainer.updateMacros([...editProfileContainer.macros]);
    };

    const handleRemoveCommand = (_command: string, macro: MacroDisplayModel, index: number) => {
        editProfileContainer.setHasChanges(true);
        const c = macro.commands[index];
        macro.commands.splice(index, 1);
        editProfileContainer.updateMacros([...editProfileContainer.macros]);
        if (c.commandType === 'hotkey') {
            editProfileContainer.removeHotkeyFromMacro(c);
        }
    };

    const handleAddCommand = (macro: MacroDisplayModel) => {
        editProfileContainer.setHasChanges(true);
        setMacroToAddCommand(macro);
    };

    const handleAddCommandWithType = (macro: MacroDisplayModel | undefined, commandType: CommandType) => {
        setMacroToAddCommand(undefined);
        editProfileContainer.setHasChanges(true);
        macro?.commands.push({ command: '', commandType });
        editProfileContainer.updateMacros([...editProfileContainer.macros]);
    };

    return {
        // state
        filter: editProfileContainer.filter,
        macroToAddCommand,
        setMacroToAddCommand,
        editingMacroIndex,
        setEditingMacroIndex,
        // search
        handleSearchChange,
        handleClearSearch,
        // macro
        copyMacro,
        handleRemoveMacro,
        handleMacroChange,
        handleNameChange,
        // steps
        handleAddStep,
        // commands
        handleCommandChange,
        handleRemoveCommand,
        handleAddCommand,
        handleAddCommandWithType,
    };
}
